import * as React from 'react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';
import { Button } from '../button';

export type Option<T extends string | null> = {
  value: T;
  label: React.ReactNode;
  icon?: React.ReactNode;
  hotkey?: string;
  abbreviation?: string;
  searchTerms?: string[];
};

interface ComboboxProps<T extends string> {
  endAdornment: React.ReactNode;
  placeholder: string;
  emptyMessage: React.ReactNode | string;
  currentOption?: Option<T>;
  options: Option<T>[];
  options2?: Option<T>[];
  options3?: Option<T>[];
  onChange: (value: T) => void;
  footerOptions?: React.ReactNode;
  setOpen: (open: boolean) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  stayOpenAfterSelection?: boolean;
  search?: string;
  setSearch?: (search: string) => void;
  alwaysShowValues?: string[];
}

const OptionsCommandGroup = <T extends string>({
  options,
  currentOption,
  onChange,
  stayOpenAfterSelection,
  setOpen,
  className,
}: {
  options: Option<T>[];
  currentOption?: Option<T>;
  onChange: (value: T) => void;
  stayOpenAfterSelection: boolean;
  setOpen: (open: boolean) => void;
  className?: string;
}) => {
  return (
    <CommandGroup className={className}>
      {options.map((option: Option<T>) => {
        const isSelected: boolean = currentOption?.value === option.value;
        return (
          <CommandItem
            key={option.value}
            value={option.value}
            keywords={option.searchTerms ?? []}
            onSelect={() => {
              onChange(option.value);
              if (!stayOpenAfterSelection) {
                setOpen(false);
              }
            }}
            className="flex flex-row justify-between items-center"
          >
            <div className="flex flex-row gap-2 items-center text-sm">
              {option.icon}
              {option.label}
            </div>
            <div className="flex flex-row gap-1">
              {isSelected && <Check className="h-3.5 w-3.5 ml-2 flex" />}
              {option.hotkey && (
                <Button variant="ghost" className="h-2 w-1 px-2 text-sm">
                  {option.hotkey}
                </Button>
              )}
            </div>
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
};

export function Combobox<T extends string>({
  endAdornment,
  placeholder,
  emptyMessage,
  currentOption,
  options,
  options2,
  options3,
  onChange,
  footerOptions,
  setOpen,
  onKeyDown,
  stayOpenAfterSelection = false,
  search,
  setSearch,
  alwaysShowValues = [],
}: ComboboxProps<T>) {
  return (
    <Command
      onKeyDown={onKeyDown}
      className="h-full"
      defaultValue={currentOption?.value || undefined}
      filter={(value, search, keywords) => {
        const query = search.toLowerCase();

        if (alwaysShowValues.includes(value)) return 1;

        if (!query) return 1;

        if (value.toLowerCase().includes(query)) return 1;

        if (keywords?.some((k) => k?.toLowerCase().includes(query))) return 1;

        return 0;
      }}
    >
      <CommandInput
        placeholder={placeholder}
        hideAdornment
        endAdornment={endAdornment}
        wrapperClassName="border-none shadow-none h-8 bg-transparent text-sm pl-2 pr-3"
        className="text-gray-500 py-0 placeholder:text-gray-400 text-sm bg-transparent"
        value={search}
        onValueChange={setSearch}
      />
      <CommandList className={cn('border-t', options2 && 'max-h-[800px]')}>
        <CommandEmpty className="text-gray-400 text-sm p-2">
          {emptyMessage}
        </CommandEmpty>
        <OptionsCommandGroup
          options={options}
          currentOption={currentOption}
          onChange={onChange}
          stayOpenAfterSelection={stayOpenAfterSelection}
          setOpen={setOpen}
        />
        {options2 && (
          <OptionsCommandGroup
            className="border-t"
            options={options2}
            currentOption={currentOption}
            onChange={onChange}
            stayOpenAfterSelection={stayOpenAfterSelection}
            setOpen={setOpen}
          />
        )}
        {options3 && (
          <OptionsCommandGroup
            className="border-t"
            options={options3}
            currentOption={currentOption}
            onChange={onChange}
            stayOpenAfterSelection={stayOpenAfterSelection}
            setOpen={setOpen}
          />
        )}
        {footerOptions}
      </CommandList>
    </Command>
  );
}
